import "../css/form.css";
import { useState, useEffect, useRef } from "react";
import consts from "../consts.js";

function Form({ language }) {
  // Compute placeholders from the `language` prop so they update automatically
  // whenever the prop changes. This avoids syncing state with props.
  const phName =
    language === consts.ENGLISH ? consts.Name_English : consts.Name_Espanol;
  const phPhone =
    language === consts.ENGLISH ? consts.Phone_English : consts.Phone_Espanol;
  const phEmail =
    language === consts.ENGLISH ? consts.Email_English : consts.Email_Espanol;
  const phType =
    language === consts.ENGLISH
      ? consts.EventType_English
      : consts.EventType_Espanol;
  const phDuration =
    language === consts.ENGLISH
      ? consts.ReserveDuration_English
      : consts.ReserveDuration_Espanol;
  const phSubmit =
    language === consts.ENGLISH ? consts.Submit_English : consts.Submit_Espanol;
  const sRespons =
    language === consts.ENGLISH
      ? consts.Response_Success_English
      : consts.Response_Success_Espanol;
  const eRespons =
    language === consts.ENGLISH
      ? consts.Response_Error_English
      : consts.Response_Error_Espanol;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [eventType, setEventType] = useState("");
  const [duration, setDuration] = useState("");
  const [date, setDate] = useState("");

  const [errorEmail, setErrorEmail] = useState(false);
  const [errorDate, setErrorDate] = useState(false);
  const [errorName, setErrorName] = useState(false);
  const [errorPhone, setErrorPhone] = useState(false);
  const [errorEventType, setErrorEventType] = useState(false);
  const [errorDuration, setErrorDuration] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const statusTimeoutRef = useRef();

  useEffect(() => {
    if (submitStatus) {
      statusTimeoutRef.current = setTimeout(() => {
        setSubmitStatus("");
      }, 5000);
    }
    return () => clearTimeout(statusTimeoutRef.current);
  }, [submitStatus]);

  function validateForm() {
    let isValid = true;

    // Check all fields have values
    if (!name.trim()) {
      setErrorName(true);
      isValid = false;
    } else {
      setErrorName(false);
    }

    if (!phone.trim()) {
      setErrorPhone(true);
      isValid = false;
    } else {
      setErrorPhone(false);
    }

    if (!email.trim()) {
      setErrorEmail(true);
      isValid = false;
    } else {
      // validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(email);
      if (!isValidEmail) {
        setErrorEmail(true);
        isValid = false;
      } else {
        setErrorEmail(false);
      }
    }

    if (!eventType.trim()) {
      setErrorEventType(true);
      isValid = false;
    } else {
      setErrorEventType(false);
    }

    if (!date) {
      setErrorDate(true);
      isValid = false;
    } else {
      // Validate date is not in the past
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison

      if (selectedDate < today) {
        setErrorDate(true);
        isValid = false;
      } else {
        setErrorDate(false);
      }
    }

    return isValid;
  }

  async function submitForm() {
    if (!validateForm() || loading) {
      return;
    }
    setLoading(true);
    const formData = {
      name,
      phone,
      email,
      eventType,
      duration,
      date,
    };
    try {
      const response = await fetch(
        "https://rls9s6uc18.execute-api.us-east-1.amazonaws.com/Prod/request",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "2MTJWktRTB1LL8J2a02851wphhuYwbmF4FuRHaKM",
          },
          body: JSON.stringify(formData),
        }
      );
      if (response.ok) {
        setSubmitStatus(sRespons);
        setName("");
        setPhone("");
        setEmail("");
        setEventType("");
        setDuration("");
        setDate("");
      } else {
        setSubmitStatus(eRespons);
      }
    } catch (err) {
      setSubmitStatus(eRespons);
    } finally {
      setLoading(false);
    }
  }

  // Handle email input change - clear error on any change
  function handleEmailChange(e) {
    setEmail(e.target.value);
    setErrorEmail(false); // Clear error when user edits
  }

  // Handle date input change - clear error on any change
  function handleDateChange(e) {
    setDate(e.target.value);
    setErrorDate(false); // Clear error when user edits
  }

  // Handle name change - clear error on any change
  function handleNameChange(e) {
    setName(e.target.value);
    setErrorName(false);
  }

  // Handle phone change - clear error on any change
  function handlePhoneChange(e) {
    setPhone(e.target.value);
    setErrorPhone(false);
  }

  // Handle event type change - clear error on any change
  function handleEventTypeChange(e) {
    setEventType(e.target.value);
    setErrorEventType(false);
  }

  // Handle duration change - clear error on any change
  function handleDurationChange(e) {
    setDuration(e.target.value);
    setErrorDuration(false);
  }

  return (
    <div className="Form">
      <input
        type="text"
        placeholder={phName}
        value={name}
        onChange={handleNameChange}
        style={
          errorName
            ? { borderColor: "red", backgroundColor: "rgba(255, 0, 0, 0.1)" }
            : {}
        }
      />
      <input
        type="text"
        placeholder={phPhone}
        value={phone}
        onChange={handlePhoneChange}
        style={
          errorPhone
            ? { borderColor: "red", backgroundColor: "rgba(255, 0, 0, 0.1)" }
            : {}
        }
      />
      <input
        type="text"
        placeholder={phEmail}
        value={email}
        onChange={handleEmailChange}
        style={
          errorEmail
            ? { borderColor: "red", backgroundColor: "rgba(255, 0, 0, 0.1)" }
            : {}
        }
      />
      <input
        type="text"
        placeholder={phType}
        value={eventType}
        onChange={handleEventTypeChange}
        style={
          errorEventType
            ? { borderColor: "red", backgroundColor: "rgba(255, 0, 0, 0.1)" }
            : {}
        }
      />
      <input
        type="date"
        placeholder={phName}
        value={date}
        onChange={handleDateChange}
        style={
          errorDate
            ? { borderColor: "red", backgroundColor: "rgba(255, 0, 0, 0.1)" }
            : {}
        }
      />
      <div
        className="submit"
        onClick={submitForm}
        style={{
          opacity: loading ? 0.6 : 1,
          pointerEvents: loading ? "none" : "auto",
          position: "relative",
        }}
      >
        {loading ? (
          <span>
            <span
              className="spinner"
              style={{
                display: "inline-block",
                width: "1em",
                height: "1em",
                border: "2px solid #fff",
                borderTop: "2px solid #9d4edd",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginRight: "8px",
                verticalAlign: "middle",
              }}
            ></span>
            {phSubmit}
          </span>
        ) : (
          phSubmit
        )}
      </div>
      {submitStatus && (
        <div
          style={{
            marginTop: "12px",
            color:
              submitStatus.startsWith("Thanks") ||
              submitStatus.startsWith("¡Gracias!")
                ? "#4caf50"
                : "#f44336",
            cursor: "pointer",
          }}
          onClick={() => setSubmitStatus("")}
        >
          {submitStatus}
        </div>
      )}
    </div>
  );
}

export default Form;
